import React, { useRef, useState } from "react";
import FanAvatar from "./FanAvatar";
import FanPointsPopup from "./FanPointsPopup";
import LevelRing from "./LevelRing";
import VerifiedTick from "./VerifiedTick";
import { useFitText } from "../hooks/useFitText";
import { t } from "../i18n";
import "./FanProfileHero.css";

// The top of My Profile (founder's mock, 2026-11-02): the photo with its level
// ring, the name with the tick or a small Verify beside it, the handle, the two
// counts with the points between them. The ring takes the level's color and the
// points say the level; a tap on the points explains them. A tap on the photo
// or the name opens the Edit Profile slider. Verify is
// a real button, so the area around it can't be one: the photo is that area's
// button for the keyboard and screen readers, and its click bubbles up.

export const PENCIL = "M4 20h4L19 9l-4-4L4 16v4Zm12.5-16.5 4 4";

type Props = {
  name: string;
  /** "@handle", or "" when there is none to show under the name. */
  handle: string;
  verified: boolean;
  showVerify: boolean;
  avatarUrl?: string | null;
  showBadge: boolean;
  level?: number | null;
  /** The counts and points; null hides them (signed out, or still loading). */
  counts: { requests: number; played: number; points: number; boostCost: number } | null;
  onOpen: () => void;
  onVerify?: () => void;
};

const FanProfileHero: React.FC<Props> = ({ name, handle, verified, showVerify, avatarUrl, showBadge, level, counts, onOpen, onVerify }) => {
  const nameRef = useRef<HTMLSpanElement>(null);
  const [pointsOpen, setPointsOpen] = useState(false);
  const lv = Math.floor(Number(level) || 0);
  const shown = name || t("fanRequestsPanel.myProfile");
  // One line: a long name shrinks first, then ends in "…"; the title has it whole.
  useFitText(nameRef, `${shown}|${verified}|${showVerify}`, 15);
  return (
    <>
      <div className="fps-name-row fph" onClick={onOpen}>
        <button type="button" className="fph-photo" aria-label={t("fanRequestsPanel.myProfile")}>
          <LevelRing level={level} hideNumber>
            <FanAvatar className="fps-avatar fph-avatar" name={name} avatarUrl={avatarUrl} showBadge={showBadge} silhouetteSize={44} />
          </LevelRing>
          <span className="fph-pencil" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d={PENCIL} />
            </svg>
          </span>
        </button>
        <span className="fph-name-line">
          <span ref={nameRef} className="fps-name" title={shown}>
            {shown}
          </span>
          {verified && <VerifiedTick className="fps-tick" label={t("fanConnect.verified")} />}
          {showVerify && (
            <button
              type="button"
              className="fps-verify"
              onClick={(e) => {
                e.stopPropagation();
                onVerify?.();
              }}
            >
              {t("editProfileSheet.verify")}
              <span aria-hidden="true">
                <VerifiedTick label="" />
              </span>
            </button>
          )}
        </span>
        {handle && <span className="fps-ig">{handle}</span>}
      </div>
      {counts && (
        <div className="fph-stats">
          <span className="fph-count">
            <b>{counts.requests.toLocaleString()}</b>
            {t("bottomTabBar.requests")}
          </span>
          <button
            type="button"
            className={`fph-points fph-points--${Math.min(lv, 5)}`}
            onClick={() => setPointsOpen(true)}
          >
            <span className="fph-points-num">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.2l-6.1 3.4 1.4-6.8L2.2 9.1l6.9-.8z" />
              </svg>
              {t("fanProfileEditorSheet.pts", { points: counts.points.toLocaleString() }).trim()}
            </span>
            {lv > 0 && <span className="fph-points-level">{t("fanProfilePoints.level", { level: lv })}</span>}
          </button>
          <span className="fph-count">
            <b>{counts.played.toLocaleString()}</b>
            {t("fanStatusPill.played")}
          </span>
        </div>
      )}
      {counts && pointsOpen && <FanPointsPopup points={counts.points} level={lv} boostCost={counts.boostCost} onClose={() => setPointsOpen(false)} />}
    </>
  );
};

export default FanProfileHero;
